import { getExistingCheckout } from '../../util';
import { formattedPrice } from './util';
import { didSurfsideLoad, trackSurfsideEvent } from '../Surfside/util';
import { gtmTrackEvent, trackEcommerceEvent } from '../../analytics';
import {
    useUpdateQuantity,
    useRemoveItem,
} from '../../hooks/dutchie-mutations';

const CartProductVariants = ({
    items,
    pricingType,
    setErrors,
    surfside,
    analytics,
}) => {
    const checkout = getExistingCheckout();

    function handleRemoveTracking(surfside, analytics, item, currentVariant) {
        // Track Surfside event.
        if (surfside && surfside.enable) {
            console.log('surfside enabled, removing');
            didSurfsideLoad(() => {
                trackSurfsideEvent(
                    'remove',
                    item.product,
                    currentVariant,
                    item.quantity
                );
            });
        }

        // Track GTM event.
        if (analytics && analytics.gtm_id && item && currentVariant) {
            gtmTrackEvent('remove_from_cart', item.product, currentVariant, item.quantity);
        }

        // Track event internally/Sentry.
        trackEcommerceEvent(
            'remove',
            item.product,
            currentVariant,
            item.quantity
        );
    }
    
    function handleUpdateQuantityTracking(item, currentVariant, newQuantity) {
        const quantityDifference = newQuantity - item.quantity;
        const eventType = quantityDifference > 0 ? 'add_to_cart' : 'remove_from_cart';
        const absQuantityDifference = Math.abs(quantityDifference);
    
        // Track Surfside event.
        if (surfside && surfside.enable) {
            didSurfsideLoad(() => {
                trackSurfsideEvent(
                    eventType === 'add_to_cart' ? 'add' : 'remove',
                    item.product,
                    currentVariant,
                    absQuantityDifference
                );
            });
        }
    
        // Track GTM event.
        if (analytics && analytics.gtm_id && item) {
            gtmTrackEvent(eventType, item.product, currentVariant, absQuantityDifference);
        }
    
        // Track event internally/Sentry.
        trackEcommerceEvent(
            eventType === 'add_to_cart' ? 'add' : 'remove',
            item.product,
            currentVariant,
            absQuantityDifference
        );
    }

    const handleRemoveItem = useRemoveItem(
        checkout.retailerId,
        setErrors,
        handleRemoveTracking,
        surfside,
        analytics
    );

    const handleUpdateQuantity = useUpdateQuantity(
        checkout.retailerId,
        checkout.checkoutId,
        null,
        setErrors
    );
    
    const handleKeyPress = (e, callback) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            callback();
        }
    };

    return (
        <div role='list' className='cart-variants-list'>
            {items.map((item, _, arr) => {
                const currentVariantId = `${item.productId}~${item.option}`;
                const currentVariant = item.product.variants.find(
                    (variant) => variant.id === currentVariantId
                );

                if (!currentVariant) return null;

                const regularPrice = pricingType.standard === 'priceMed'
                    ? currentVariant.priceMed
                    : currentVariant.priceRec;

                const specialPrice = pricingType.special === 'specialPriceMed'
                    ? currentVariant.specialPriceMed
                    : currentVariant.specialPriceRec;

                const finalPrice = (specialPrice !== null && specialPrice < regularPrice)
                    ? specialPrice
                    : regularPrice;

                const totalPrice = finalPrice * item.quantity;
                const hasDiscount = specialPrice !== null && specialPrice < regularPrice;

                return (
                    <div 
                        role='listitem' 
                        className='cart-variant-single' 
                        key={currentVariantId}
                    >
                        <div aria-label={`Product option: ${item.option}`}>
                            {item.option !== 'N/A' ? item.option : null}
                        </div>
                        <div className='cart-variant-single-end'>
                            <button
                                className='cart-variant-remove'
                                onClick={() =>
                                    handleRemoveItem.mutate({
                                        retailerId: checkout.retailerId,
                                        checkoutId: checkout.checkoutId,
                                        item,
                                        currentVariant,
                                    })
                                }
                                onKeyPress={(e) =>
                                    handleKeyPress(e, () =>
                                        handleRemoveItem.mutate({
                                            retailerId: checkout.retailerId,
                                            checkoutId: checkout.checkoutId,
                                            item,
                                            currentVariant,
                                        })
                                    )
                                }
                                aria-label={`Remove ${item.product.name} ${item.option} from cart`}
                            >
                                <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    viewBox='0 0 448 512'
                                    aria-hidden='true'
                                    focusable='false'
                                >
                                    <path d='M170.5 51.6L151.5 80h145l-19-28.4c-1.5-2.2-4-3.6-6.7-3.6H177.1c-2.7 0-5.2 1.3-6.7 3.6zm147-26.6L354.2 80h13.7H416h8c13.3 0 24 10.7 24 24s-10.7 24-24 24H412.4L388.4 452.7c-2.5 33.4-30.3 59.3-63.8 59.3H123.4c-33.5 0-61.3-25.9-63.8-59.3L35.6 128H24c-13.3 0-24-10.7-24-24S10.7 80 24 80h8H80.1 93.8l36.7-55.1C140.9 9.4 158.4 0 177.1 0h93.7c18.7 0 36.2 9.4 46.6 24.9zM83.7 128l23.8 321.2c.6 8.4 7.6 14.8 16 14.8H324.6c8.4 0 15.3-6.5 16-14.8L364.3 128H83.7zm62.2 81.9c7.8-7.8 20.5-7.8 28.3 0L224 259.7l49.9-49.9c7.8-7.8 20.5-7.8 28.3 0s7.8 20.5 0 28.3L252.3 288l49.9 49.9c7.8 7.8 7.8 20.5 0 28.3s-20.5 7.8-28.3 0L224 316.3l-49.9 49.9c-7.8 7.8-20.5 7.8-28.3 0s-7.8-20.5 0-28.3L195.7 288l-49.9-49.9c-7.8-7.8-7.8-20.5 0-28.3z' />
                                </svg>
                            </button>
                            <label 
                                className='sr-only' 
                                htmlFor={`quantity-${currentVariantId}`}
                            >
                                Quantity for {item.product.name} {item.option}
                            </label>
                            <select
                                id={`quantity-${currentVariantId}`}
                                className='form-select headless-dropdown cart-quantity-selector'
                                value={item.quantity}
                                onChange={(e) => {
                                    const newQuantity = Number(e.target.value);
                                    handleUpdateQuantityTracking(item, currentVariant, newQuantity);
                                    handleUpdateQuantity.mutate({
                                        retailerId: checkout.retailerId,
                                        checkoutId: checkout.checkoutId,
                                        itemId: item.id,
                                        quantity: newQuantity
                                    });
                                }}
                                aria-label={`Quantity for ${item.product.name} ${item.option}`}
                            >
                                {[...Array(Math.max(currentVariant.quantity, item.quantity))].map((_, i) => (
                                    <option value={i + 1} key={i + 1}>
                                        {i + 1}
                                    </option>
                                ))}
                            </select>
                            <div 
                                className='headless-cart-item-price'
                                aria-live='polite'
                            >
                                {hasDiscount ? (
                                    <div 
                                        className='headless-cart-price-wrap'
                                        aria-label={`Original price ${formattedPrice(regularPrice * item.quantity)}, discounted to ${formattedPrice(totalPrice)}`}
                                    >
                                        <div className='headless-cart-price-regular'>
                                            <s 
                                                className='headless-cart-item-price__strikethrough'
                                                aria-hidden='true'
                                            >
                                                {formattedPrice(regularPrice * item.quantity)}
                                            </s>
                                        </div>
                                        <div 
                                            className='headless-cart-price-discount'
                                            aria-hidden='true'
                                        >
                                            {formattedPrice(totalPrice).replace('.00', '')}
                                        </div>
                                    </div>
                                ) : (
                                    <span aria-label={`Price: ${formattedPrice(totalPrice)}`}>
                                        {formattedPrice(totalPrice)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default CartProductVariants;
